// PasskeyLoginButton — U67 (locker-pwa-passkey Phase 2.5)
// 잠금 화면에서 패스키 인증을 트리거하는 버튼 컴포넌트.
//
// 노출 조건:
//   1. 브라우저가 WebAuthn을 지원한다 (detectCapabilities().available)
//   2. 서버에 등록된 패스키가 1개 이상 존재한다
//      → authBegin 응답의 allowCredentials 배열이 비어 있지 않은 것으로 판단
//
// 인증 흐름:
//   버튼 클릭 → authenticatePasskeyFlow() → 성공 시 onSuccess() 콜백 호출
//   실패(사용자 취소 / 기기 오류) → 한국어 에러 토스트

import { useState, useEffect, useCallback } from 'react';
import { Fingerprint, Loader2 } from 'lucide-react';
import { detectCapabilities } from '../utils/webauthn';
import {
  authenticatePasskeyFlow,
  passkeyApi,
} from '../api/passkey';
import {
  WebauthnUserCancelledError,
  WebauthnNotSupportedError,
} from '../utils/webauthn';
import { useToast } from './Toast';

/** PasskeyLoginButton 외부 Props */
export interface PasskeyLoginButtonProps {
  /** 패스키 인증 성공 시 호출되는 콜백 */
  onSuccess: () => void;
  /**
   * 버튼 가시성 결정 이후 부모에게 알림 (선택).
   * visible=true → 패스키 사용 가능 + 등록된 패스키 존재.
   * visible=false → WebAuthn 미지원 또는 등록 패스키 없음.
   */
  onVisibilityChange?: (visible: boolean) => void;
  /**
   * 외부에서 자동 트리거 여부를 제어한다 (PWA standalone 감지).
   * true로 변경되면 컴포넌트가 마운트 후 자동으로 인증을 시작한다.
   */
  autoTrigger?: boolean;
}

/**
 * 패스키 인증 버튼.
 *
 * - 마운트 시 WebAuthn 능력 + 등록 패스키 존재 여부를 체크한다.
 * - 조건 충족 시 화면에 노출, 그 외에는 null 반환 (UI 공간 미차지).
 * - autoTrigger=true 시 자동으로 authenticatePasskeyFlow()를 호출한다.
 */
export function PasskeyLoginButton({
  onSuccess,
  onVisibilityChange,
  autoTrigger = false,
}: PasskeyLoginButtonProps) {
  /** 버튼 노출 여부 — 능력+등록 확인 후 결정 */
  const [visible, setVisible] = useState(false);
  /** 능력·등록 체크 완료 여부 */
  const [checked, setChecked] = useState(false);
  /** 인증 진행 중 */
  const [pending, setPending] = useState(false);
  const toast = useToast();

  // ── 능력 + 등록 패스키 존재 여부 체크 ──────────────────────
  useEffect(() => {
    let cancelled = false;

    async function check() {
      try {
        // 1단계: 브라우저 WebAuthn 지원 여부
        const caps = await detectCapabilities();
        if (!caps.available) {
          if (!cancelled) {
            setVisible(false);
            setChecked(true);
            onVisibilityChange?.(false);
          }
          return;
        }

        // 2단계: 등록된 패스키 존재 여부 — authBegin allowCredentials 확인
        // authBegin은 allowCredentials가 비어있으면 등록된 패스키 없음을 의미한다.
        let hasCredentials = false;
        try {
          const options = await passkeyApi.authBegin();
          // allowCredentials 배열이 비어있지 않으면 등록된 패스키 있음
          const opts = options as { allowCredentials?: unknown[] };
          hasCredentials =
            Array.isArray(opts.allowCredentials) &&
            opts.allowCredentials.length > 0;
        } catch {
          // authBegin 실패(서버 오류 등) → 등록 패스키 없는 것으로 간주
          hasCredentials = false;
        }

        if (!cancelled) {
          setVisible(hasCredentials);
          setChecked(true);
          onVisibilityChange?.(hasCredentials);
        }
      } catch {
        if (!cancelled) {
          setVisible(false);
          setChecked(true);
          onVisibilityChange?.(false);
        }
      }
    }

    void check();
    return () => { cancelled = true; };
  }, [onVisibilityChange]);

  // ── 인증 실행 ─────────────────────────────────────────────
  const handleAuthenticate = useCallback(async () => {
    if (pending) return;
    setPending(true);
    try {
      await authenticatePasskeyFlow();
      onSuccess();
    } catch (err) {
      if (err instanceof WebauthnUserCancelledError) {
        // 사용자가 직접 취소 — 조용히 처리 (toast 없음)
      } else if (err instanceof WebauthnNotSupportedError) {
        toast.error('이 브라우저는 패스키를 지원하지 않습니다.');
      } else {
        // 기기 오류 또는 서버 오류
        const message =
          err instanceof Error ? err.message : '패스키 인증에 실패했습니다.';
        toast.error(`패스키 오류: ${message}`);
      }
    } finally {
      setPending(false);
    }
  }, [pending, onSuccess, toast]);

  // ── 자동 트리거 (PWA standalone) ──────────────────────────
  useEffect(() => {
    if (autoTrigger && visible && checked && !pending) {
      void handleAuthenticate();
    }
    // autoTrigger가 true로 바뀌는 시점 1회만 실행
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoTrigger, visible, checked]);

  // 조건 미충족 → 렌더 없음
  if (!checked || !visible) return null;

  return (
    <button
      type="button"
      className="btn btn--passkey"
      onClick={() => void handleAuthenticate()}
      disabled={pending}
      aria-label="패스키로 로그인"
      data-testid="passkey-login-button"
    >
      {pending ? (
        <Loader2 className="animate-spin" size={18} aria-hidden />
      ) : (
        <Fingerprint size={18} aria-hidden />
      )}
      <span>{pending ? '인증 중…' : '패스키로 로그인'}</span>
    </button>
  );
}
